import React, { useState } from "react";
import { Link } from "react-router-dom";
import DesignPreviewShell from "@/components/DesignPreviewShell";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * Field Radar × ContractorPlus — Work Orders Preview
 *
 * READ-ONLY VISUAL PROTOTYPE. Board / list / calendar toggle, colored status
 * counter strip, filter row, and a kanban board with WO cards.
 *
 * Route: /admin/design-preview/jobs
 */

const STATUSES = [
  { k: "pending",     label: "Pending",       color: "#F5B93F", count: 42 },
  { k: "scheduled",   label: "Scheduled",     color: "#F97316", count: 18 },
  { k: "assigned",    label: "Assigned",      color: "#FF6A2B", count: 24 },
  { k: "in_progress", label: "In Progress",   color: "#B12FBB", count: 11 },
  { k: "photos_in",   label: "Photos In",     color: "#3B82F6", count: 7  },
  { k: "completed",   label: "Completed",     color: "#22C6A6", count: 128 },
  { k: "emergency",   label: "Emergency",     color: "#E64C5A", count: 3  },
  { k: "parts_wait",  label: "Parts Ordered", color: "#8B5CF6", count: 6  },
];

const CARDS = {
  pending: [
    { id: "3716", title: "Bathroom faucet drip", prop: "1230 Anyon St", tech: "?", techColor: "#8B93A7", ago: "12m ago", pri: "med", tenant: "Test T.", src: "AF" },
    { id: "3715", title: "Storm door hinge loose", prop: "412 Front St · 2B", tech: "?", techColor: "#8B93A7", ago: "38m ago", pri: "low", tenant: "S. Chen", src: "CU" },
    { id: "3714", title: "AC not cooling", prop: "88 W Girard", tech: "?", techColor: "#8B93A7", ago: "1h ago", pri: "high", tenant: "R. Kim", src: "MMS" },
  ],
  scheduled: [
    { id: "3713", title: "Replace GFCI outlet", prop: "1547 N 7th", tech: "M", techColor: "#3B82F6", ago: "Tue 9am", pri: "med", tenant: "J. Park", src: "AF" },
    { id: "3712", title: "Paint touch-up · turn", prop: "88 W Girard · 3A", tech: "J", techColor: "#22C6A6", ago: "Tue 2pm", pri: "low", tenant: "—", src: "PMR" },
  ],
  assigned: [
    { id: "3711", title: "Garbage disposal", prop: "1230 Anyon St", tech: "J", techColor: "#22C6A6", ago: "Today", pri: "med", tenant: "Test T.", src: "AF", partsWait: false },
    { id: "3710", title: "Snow-plow driveway", prop: "412 Front St", tech: "M", techColor: "#3B82F6", ago: "Today", pri: "med", tenant: "—", src: "Snow" },
    { id: "3709", title: "Ceiling stain · roof leak?", prop: "88 W Girard · 2A", tech: "R", techColor: "#F97316", ago: "Today", pri: "high", tenant: "R. Kim", src: "MMS" },
  ],
  in_progress: [
    { id: "3708-1", title: "Water heater replacement", prop: "1547 N 7th", tech: "J", techColor: "#22C6A6", ago: "started 8:42am", pri: "high", tenant: "J. Park", src: "AF", live: true },
    { id: "3707-1", title: "Kitchen sink clog", prop: "88 W Girard · 1A", tech: "M", techColor: "#3B82F6", ago: "started 9:15am", pri: "med", tenant: "L. Ortiz", src: "AF", live: true },
  ],
  photos_in: [
    { id: "3706-1", title: "Deadbolt swap", prop: "1230 Anyon St", tech: "M", techColor: "#3B82F6", ago: "6 photos · needs review", pri: "med", tenant: "Test T.", src: "AF" },
  ],
  completed: [
    { id: "3705", title: "Dryer vent clean", prop: "412 Front St · 3B", tech: "J", techColor: "#22C6A6", ago: "Yesterday", pri: "low", tenant: "K. Lu", src: "AF" },
    { id: "3704", title: "Toilet flapper", prop: "1547 N 7th · 1A", tech: "R", techColor: "#F97316", ago: "Yesterday", pri: "low", tenant: "N. Diaz", src: "AF" },
  ],
};

export default function WorkOrdersPreview() {
  const { theme } = useDesignTheme();
  const [view, setView] = useState("board");
  const [statusFilter, setStatusFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  return (
    <DesignPreviewShell>
      <div className="font-sans">
        {/* ── HERO ─────────────────────────────────────────── */}
        <div className={`${theme.gradient} pt-6 pb-28 sm:pt-8 sm:pb-32 px-4 sm:px-6 relative`}>
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest">
              <Link to="/admin/design-preview" className="hover:text-white">← Dashboard</Link>
              <span>·</span>
              <span>Work Orders</span>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
              <div>
                <h1 className="text-white font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                  Work Orders
                </h1>
                <div className="text-white/80 text-xs sm:text-sm font-medium mt-1">
                  <b>239 total</b> · 128 open · 42 pending · <span className="text-white font-black">3 emergency</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="inline-flex bg-white/10 backdrop-blur-md rounded-full p-1">
                  {[
                    { k: "board", label: "Board", icon: "▦" },
                    { k: "list",  label: "List",  icon: "≡" },
                    { k: "cal",   label: "Cal",   icon: "▤" },
                  ].map(v => (
                    <button
                      key={v.k}
                      onClick={() => setView(v.k)}
                      data-testid={`dp-wo-view-${v.k}`}
                      className={`h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${
                        view === v.k ? "bg-white text-[#0F1F3A]" : "text-white/80 hover:text-white"
                      }`}
                    >
                      <span className="text-sm leading-none">{v.icon}</span>
                      <span>{v.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  data-testid="dp-wo-new"
                  className="h-9 px-4 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                >
                  + New WO
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── FLOATING TOOLBAR ────────────────────────────── */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 -mt-16 pb-6 relative z-10">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-4">
            {/* Status counter chips */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="dp-wo-status-strip">
              <button
                onClick={() => setStatusFilter(null)}
                className={`flex-shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 transition-colors ${
                  !statusFilter ? "bg-[#0F1F3A] border-[#0F1F3A] text-white" : "border-slate-200 text-[#0F1F3A] hover:bg-slate-50"
                }`}
              >
                <span className="text-xs font-black">All</span>
                <span className={`text-xs font-black ${!statusFilter ? "text-[#00C4CC]" : "text-[#8B93A7]"}`}>239</span>
              </button>
              {STATUSES.map(s => {
                const active = statusFilter === s.k;
                return (
                  <button
                    key={s.k}
                    onClick={() => setStatusFilter(active ? null : s.k)}
                    data-testid={`dp-wo-filter-${s.k}`}
                    className="flex-shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 hover:bg-slate-50 transition-colors"
                    style={{
                      borderColor: active ? s.color : s.color + "44",
                      background: active ? s.color + "12" : "transparent",
                    }}
                  >
                    <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: s.color }}/>
                    <span className="text-xs font-bold text-[#0F1F3A]">{s.label}</span>
                    <span className="text-xs font-black" style={{ color: s.color }}>{s.count}</span>
                  </button>
                );
              })}
            </div>

            {/* Filter row */}
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <input
                  type="text"
                  placeholder="Search WO #, property, tenant, description…"
                  className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-50 border border-slate-200 text-sm text-[#0F1F3A] placeholder:text-[#8B93A7] focus:outline-none focus:border-[#00C4CC] focus:bg-white"
                  data-testid="dp-wo-search"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B93A7] text-sm">🔍</span>
              </div>
              {["Property ⌄", "Assignee ⌄", "Date range ⌄", "Source ⌄", "Priority ⌄"].map(f => (
                <button
                  key={f}
                  className="h-9 px-3 rounded-full border border-slate-200 hover:bg-slate-50 text-xs font-bold text-[#0F1F3A]"
                >
                  {f}
                </button>
              ))}
              <button
                onClick={() => setShowFilters(s => !s)}
                className="h-9 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-black uppercase tracking-widest text-[#0F1F3A]"
              >
                {showFilters ? "Hide" : "More"} filters
              </button>
              <div className="ml-auto flex items-center gap-1">
                <button className="h-9 w-9 rounded-full hover:bg-slate-100 text-[#8B93A7]" title="Export">📤</button>
                <button className="h-9 w-9 rounded-full hover:bg-slate-100 text-[#8B93A7]" title="Bulk actions">☰</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── BOARD ─────────────────────────────────────── */}
        {view === "board" && (
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-16">
            <div className="flex gap-3 overflow-x-auto pb-3" data-testid="dp-wo-board">
              {[
                { k: "pending",     label: "Pending",     color: "#F5B93F" },
                { k: "scheduled",   label: "Scheduled",   color: "#F97316" },
                { k: "assigned",    label: "Assigned",    color: "#FF6A2B" },
                { k: "in_progress", label: "In Progress", color: "#B12FBB" },
                { k: "photos_in",   label: "Photos In",   color: "#3B82F6" },
                { k: "completed",   label: "Completed",   color: "#22C6A6" },
              ].map(col => (
                <BoardColumn key={col.k} col={col} cards={CARDS[col.k] || []} />
              ))}
            </div>
          </div>
        )}

        {view === "list" && <ListView theme={theme}/>}
        {view === "cal"  && <CalendarView/>}

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-12 text-center">
          <p className="text-white/40 text-xs font-medium">
            Preview only · <Link to="/admin/design-preview" className="text-[#00C4CC]">back to dashboard preview</Link>
          </p>
        </div>
      </div>
    </DesignPreviewShell>
  );
}

/* ── Board column ─────────────────────────────────────── */
function BoardColumn({ col, cards }) {
  return (
    <div className="flex-shrink-0 w-[300px]" data-testid={`dp-wo-col-${col.k}`}>
      {/* Column header */}
      <div className="bg-white rounded-t-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] px-3 py-2.5 flex items-center gap-2 border-b-4" style={{ borderColor: col.color }}>
        <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: col.color }}/>
        <span className="text-[11px] font-black uppercase tracking-widest text-[#0F1F3A] flex-1 truncate">{col.label}</span>
        <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: col.color + "22", color: col.color }}>
          {cards.length}
        </span>
        <button className="h-6 w-6 rounded-full hover:bg-slate-100 text-[#8B93A7] text-sm leading-none">+</button>
      </div>
      {/* Cards */}
      <div className="bg-slate-50/50 backdrop-blur rounded-b-2xl p-2 space-y-2 min-h-[240px]">
        {cards.map(c => (
          <WOCard key={c.id} c={c} color={col.color}/>
        ))}
        {cards.length === 0 && (
          <div className="text-center text-[10px] font-medium text-[#8B93A7] py-8 border-2 border-dashed border-slate-200 rounded-xl">
            Drop WOs here
          </div>
        )}
      </div>
    </div>
  );
}

function WOCard({ c, color }) {
  const priColors = { high: "#E64C5A", med: "#F5B93F", low: "#8B93A7" };
  return (
    <Link
      to="/admin/design-preview/wo"
      data-testid={`dp-wo-card-${c.id}`}
      className="block bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing border border-slate-100 group"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">#{c.id}</span>
        <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ background: priColors[c.pri] + "22", color: priColors[c.pri] }}>
          {c.pri}
        </span>
        {c.src && (
          <span className="ml-auto text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7]">
            {c.src}
          </span>
        )}
      </div>
      <div className="text-sm font-black text-[#0F1F3A] leading-tight">{c.title}</div>
      <div className="text-[11px] font-medium text-[#8B93A7] mt-1 flex items-center gap-1">
        <span>📍</span>
        <span className="truncate">{c.prop}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
        <div className="h-6 w-6 rounded-full flex items-center justify-center text-white font-black text-[10px] flex-shrink-0"
          style={{ background: c.techColor }}>
          {c.tech}
        </div>
        <span className="text-[10px] font-medium text-[#8B93A7] flex-1 truncate">
          {c.live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22C6A6] mr-1 animate-pulse"/>}
          {c.ago}
        </span>
        <button className="h-6 w-6 rounded-full text-[#8B93A7] hover:bg-slate-100 text-sm leading-none opacity-0 group-hover:opacity-100 transition-opacity" title="Quick action">⋯</button>
      </div>
    </Link>
  );
}

/* ── List view ────────────────────────────────────────── */
function ListView() {
  const rows = Object.entries(CARDS).flatMap(([status, list]) =>
    list.map(c => ({ ...c, status }))
  );
  const priColors = { high: "#E64C5A", med: "#F5B93F", low: "#8B93A7" };
  const statusColor = (s) => STATUSES.find(x => x.k === s)?.color || "#8B93A7";
  const statusLabel = (s) => STATUSES.find(x => x.k === s)?.label || s;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-16" data-testid="dp-wo-list">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] overflow-hidden">
        <div className="hidden sm:grid grid-cols-[80px_1fr_180px_120px_100px_80px_100px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          {["#", "Work Order", "Property", "Status", "Tech", "Priority", "Age"].map(h => (
            <div key={h} className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{h}</div>
          ))}
        </div>
        <div className="divide-y divide-slate-50">
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-[80px_1fr_180px_120px_100px_80px_100px] gap-2 px-4 py-3 items-center hover:bg-slate-50 cursor-pointer" data-testid={`dp-wo-list-row-${r.id}`}>
              <div className="text-xs font-black text-[#8B93A7]">#{r.id}</div>
              <div className="min-w-0">
                <div className="text-sm font-black text-[#0F1F3A] truncate">{r.title}</div>
                <div className="text-[10px] font-medium text-[#8B93A7] truncate">{r.tenant} · via {r.src}</div>
              </div>
              <div className="text-xs font-medium text-[#0F1F3A] truncate">{r.prop}</div>
              <div>
                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full inline-block"
                  style={{ background: statusColor(r.status) + "22", color: statusColor(r.status) }}>
                  {statusLabel(r.status)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-white font-black text-[10px]"
                  style={{ background: r.techColor }}>{r.tech}</div>
              </div>
              <div>
                <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: priColors[r.pri] }}>{r.pri}</span>
              </div>
              <div className="text-[10px] font-medium text-[#8B93A7]">
                {r.live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22C6A6] mr-1 animate-pulse"/>}
                {r.ago}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Calendar stub ────────────────────────────────────── */
function CalendarView() {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const events = [[], [], [{ t: "3711 · Anyon", c: "#FF6A2B" }], [{ t: "3713 · N 7th", c: "#F97316" }, { t: "3712 · Girard", c: "#F97316" }], [{ t: "3710 · Front", c: "#B12FBB" }], [], []];
  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-16" data-testid="dp-wo-calendar">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-4">
        <div className="grid grid-cols-7 gap-2">
          {days.map((d, i) => (
            <div key={d} className="text-center">
              <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{d}</div>
              <div className="mt-1 aspect-square bg-slate-50 rounded-xl p-2 flex flex-col gap-1 items-start">
                <div className="text-[10px] font-black text-[#0F1F3A]">{22 + i}</div>
                {(events[i] || []).map((e, idx) => (
                  <div key={idx} className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded w-full text-white truncate" style={{ background: e.c }}>
                    {e.t}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-center text-[10px] font-medium text-[#8B93A7]">
          Week of Sep 22 · drag to reschedule (live version)
        </div>
      </div>
    </div>
  );
}
